import { Component } from '@angular/core';
import { IonicPage, NavController, NavParams, LoadingController, AlertController, Loading, Alert } from 'ionic-angular';
import { AwsProvider } from '../../providers/aws/aws';

export interface IButton
{
  buttonText: string,
  display: boolean
}

export interface IButtonGroup
{
  accept: IButton,
  essential: IButton,
  decline: IButton
}

export interface IScriptConfig
{
  formCollection: boolean,
  showPopup: boolean,
  popupText: string,
  privacyPolicy: string,
  devLive: string,
  buttons: IButtonGroup
}

@IonicPage()
@Component({
  selector: 'page-compliance-domain',
  templateUrl: 'compliance-domain.html',
})

export class ComplianceDomainPage {
  domain: object;
  domains: Array<string>
  urlConfig: object;
  users: Array<object>;
  filteredUsers: Array<object>;
  filter: string;
  changeUser: boolean;
  showDupe: number;
  addConfig: boolean;
  editConfigIndex: number;
  editConfigData: object;
  newPrefix: string;
  newBlacklist: string;
  newRequired: string;
  newUnmatch: string;
  rawTextInput: string;
  rawFieldInput: string;
  scriptConfig: IScriptConfig;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public aws: AwsProvider,
    public loadingCtrl: LoadingController,
    public alertCtrl: AlertController
  ) {
    this.domain = JSON.parse(JSON.stringify(this.navParams.get('domain')));
    this.urlConfig = { userIds: [], formConfigs: [], unmatchedForms: [] };
    this.scriptConfig = {
      formCollection: true,
      showPopup: true,
      popupText: '',
      privacyPolicy: '',
      devLive: 'dev',
      buttons: {
        accept: {
          buttonText:'Accept All',
          display:true
        },
        essential: {
          buttonText:'Essential Cookies Only',
          display:true
        },
        decline: {
          buttonText:'Forget Me',
          display:true
        }
      }
    };
    this.domains = JSON.parse(JSON.stringify(this.navParams.get('domains')));
    this.users = [];
    this.filteredUsers = [];
    this.filter = '';
    this.changeUser = false;
    this.showDupe = null;
    this.addConfig = false;
    this.editConfigIndex = null;
    this.newPrefix = '';
    this.newBlacklist = '';
    this.newRequired = '';
    this.newUnmatch = '';
    this.rawFieldInput = '';
    this.rawFieldInput = '';
  }

  async ngAfterViewInit(): Promise<void> {
    const loading: Loading = this.loadingCtrl.create({});
    loading.present();
    const promises: Array<Promise<object>> = [
      this.aws.invokeLambda('SherloqAdminFEGetUsersForDropdown:v1'),
      this.aws.invokeLambda(
        'SherloqAdminFEGetComplianceDomain:v1',
        { domain: this.domain['text'] }
      ),
      this.aws.invokeLambda(
        'SherloqAdminFEGetComplianceConfig:Active',
        { domain: this.domain['text'] }
      )
    ];
    const results: Array<object> = await Promise.all(promises);
    if (!results[0]['success'] || !results[1]['success'] || !results[2]['success']) {
      let errmsg;
      if (!results[0]['success']) {
        errmsg = results[0]['response'];
      } else if (!results[1]['success']) {
        errmsg = results[1]['response'];
      } else if (!results[2]['success']) {
        errmsg = results[2]['response'];
      }
      const alert: Alert = this.alertCtrl.create({
        message: errmsg,
        buttons: ['Ok'],
        enableBackdropDismiss: true
      });
      alert.present();
      this.navCtrl.pop();
    } else {
      this.users = results[0]['response']['users'];
      this.urlConfig = results[1]['response']['urlConfig'];
      // If the domain does not have a script config yet, use the default
      if (!this.isEmpty(results[2]['response']['scriptConfig'])) {
        this.scriptConfig = results[2]['response']['scriptConfig'];
      }
      this.filterUsers();
    }
    loading.dismiss();
  }

  /**
   * Checks whether an object is empty
   */
  isEmpty(obj): Boolean {
    return Object.keys(obj).length === 0;
  }
  /**
   * Assigns radio & select box choices to their scriptConfig field.
   */
   setField(field, value){
    let newVal: any;
    if (value === "yes") {
      newVal = true;
    } else if (value === "no"){
      newVal = false;
    } else {
      newVal = value;
    }
  	this.scriptConfig[field] = newVal;
  }

  async submitScriptConfigForm(): Promise<boolean> {
    const loading: Loading = this.loadingCtrl.create({});
    loading.present();
    const results: Promise<object> = await this.aws.invokeLambda(
      'SherloqAdminFESetComplianceConfig:Active', {
        'domain': this.domain['text'],
        'formCollection': this.scriptConfig.formCollection,
        'showPopup': this.scriptConfig.showPopup,
        'popupText': this.scriptConfig.popupText,
        'buttonAcceptDisplay': this.scriptConfig.buttons.accept.display,
        'buttonAcceptText': this.scriptConfig.buttons.accept.buttonText,
        'buttonEssentialDisplay': this.scriptConfig.buttons.essential.display,
        'buttonEssentialText': this.scriptConfig.buttons.essential.buttonText,
        'buttonDeclineDisplay': this.scriptConfig.buttons.decline.display,
        'buttonDeclineText': this.scriptConfig.buttons.decline.buttonText,
        'privacyPolicy': this.scriptConfig.privacyPolicy,
        'devLive': this.scriptConfig.devLive
      }
    );
    console.log(JSON.stringify(results));
    if (!results['success']) {
      let errmsg = results['response'];
      const alert: Alert = this.alertCtrl.create({
        message: errmsg,
        buttons: ['Ok'],
        enableBackdropDismiss: true
      });
      alert.present();
      this.navCtrl.pop();
    } else {
      const alert: Alert = this.alertCtrl.create({
        message: 'The compliance script config settings have been updated.',
        buttons: ['Ok'],
        enableBackdropDismiss: true
      });
      alert.present();
      this.navCtrl.pop();
    }
    loading.dismiss();
    return true;
  }

  async reloadAllDomainData(): Promise<boolean> {
    const result: object = await this.aws.invokeLambda(
      'SherloqAdminFEGetComplianceDomain:v1',
      { domain: this.domain['text'] }
    );
    if (!result['success']) {
      const alert: Alert = this.alertCtrl.create({
        message: result['response'],
        buttons: ['Ok'],
        enableBackdropDismiss: true
      });
      alert.present();
      return false;
    } else {
      this.urlConfig = result['response']['urlConfig'];
      return true;
    }
  }

  filterUsers(): void {
    this.filteredUsers = [];
    const filter: string = this.filter.trim().toLowerCase();
    for (let user of this.users) {
      if (filter.length === 0) {
        this.filteredUsers.push(user);
      } else if (user['email'].indexOf(filter) > -1) {
        this.filteredUsers.push(user);
      }
    }
  }

  mapUserIdToEmail(userId: string): string {
    for (let user of this.users) {
      if (user['userId'] === userId) return user['email'];
    }
    return userId;
  }

  async removeUser(notFormConfig?: boolean): Promise<void> {
    if (notFormConfig) {
      await this.editBackendDomainUser(null);
    } else {
      this.editConfigData['userIds'] = [];
    }
  }

  async editUser(user: object, notFormConfig?: boolean): Promise<void> {
    this.bringDownUsers();
    if (notFormConfig) {
      await this.editBackendDomainUser(user['userId']);
    } else {
      this.editConfigData['userIds'] = [user['userId']];
    }
  }

  bringUpUsers(notFormConfig?: boolean): void {
    if (notFormConfig && this.editConfigIndex !== null) {
      this.bringDownFormConfig();
    }
    this.changeUser = true;
    this.showDupe = null;
  }

  bringDownUsers(): void {
    this.changeUser = false;
  }

  bringUpFormConfig(index: number): void {
    if (index === null && this.addConfig) return;
    if (index !== null && this.editConfigIndex === index) return;
    if (index !== null && this.addConfig) { // must remove artifical config added to data
      this.urlConfig['formConfigs'] = this.urlConfig['formConfigs'].slice(1, this.urlConfig['formConfigs'].length);
      this.addConfig = false;
      this.editConfigIndex = null;
      index--;
    }
    this.newPrefix = '';
    this.newBlacklist = '';
    if (index === null) {
      this.editConfigData = {
        name: '',
        ignore: false,
        type: 'exact',
        prefixes: [],
        blacklistFields: [],
        requiredFields: [],
        unmatchFields: [],
        userIds: [],
        matchedForms: [],
        fields: {
          name: { text: '', required: 'true' },
          email: { text: '', required: 'true' },
          phone: { text: '', required: 'true' },
          messageParts: []
        }
      };
      this.urlConfig['formConfigs'] = [JSON.parse(JSON.stringify(this.editConfigData))].concat(this.urlConfig['formConfigs']);
      this.addConfig = true;
      this.editConfigIndex = 0;
      this.changeUser = false;
      this.toggleMatchedForm(null);
    } else {
      this.addConfig = false;
      const editConfigData: object = JSON.parse(JSON.stringify(this.urlConfig['formConfigs'][index]));
      if (!editConfigData['requiredFields']) editConfigData['requiredFields'] = [];
      if (!editConfigData['unmatchFields']) editConfigData['unmatchFields'] = [];
      if (!editConfigData['name']) editConfigData['name'] = '';
      if (!editConfigData['userIds']) editConfigData['userIds'] = [];
      if (!editConfigData['matchedForms']) editConfigData['matchedForms'] = [];
      editConfigData['fields'] = {
        name: { text: '', required: 'true' },
        email: { text: '', required: 'true' },
        phone: { text: '', required: 'true' },
        messageParts: JSON.parse(JSON.stringify(this.urlConfig['formConfigs'][index]['fields']['messageParts']))
      };
      if (this.urlConfig['formConfigs'][index]['fields']['messageParts']) {
        editConfigData['fields']['messageParts'] = JSON.parse(JSON.stringify(this.urlConfig['formConfigs'][index]['fields']['messageParts']));
      } else {
        editConfigData['fields']['messageParts'] = [];
      }
      for (let key in this.urlConfig['formConfigs'][index]['fields']) {
        if (key === 'messageParts') continue;
        if (editConfigData['fields'][this.urlConfig['formConfigs'][index]['fields'][key]['name']]) {
          editConfigData['fields'][this.urlConfig['formConfigs'][index]['fields'][key]['name']]['text'] = key;
          editConfigData['fields'][this.urlConfig['formConfigs'][index]['fields'][key]['name']]['required'] = String(this.urlConfig['formConfigs'][index]['fields'][key]['required']);
        }
      }
      this.editConfigData = editConfigData;
      this.editConfigIndex = index;
      this.showDupe = null;
      this.changeUser = false;
      this.toggleMatchedForm(null);
    }
  }

  bringDownFormConfig(): void {
    if (this.addConfig) { // must remove artifical config added to data
      this.urlConfig['formConfigs'] = this.urlConfig['formConfigs'].slice(1, this.urlConfig['formConfigs'].length);
    }
    this.addConfig = false;
    this.editConfigIndex = null;
  }

  removeFormConfig(index: number): void {
    this.editBackendFormConfigs('remove', this.urlConfig['formConfigs'][index]);
  }

  joinConfigPrefixes(formConfig: object): string {
    return formConfig['prefixes'].join(', ');
  }

  editConfigType(type: string) {
    this.editConfigData['type'] = type;
  }

  addPrefix(): void {
    const newPrefix: string = this.newPrefix.trim();
    let found: boolean = false;
    for (let prefix of this.editConfigData['prefixes']) {
      if (newPrefix === prefix) {
        found = true;
        break;
      }
    }
    if (!found && newPrefix.length !== 0) {
      this.editConfigData['prefixes'].push(newPrefix);
      this.newPrefix = '';
    }
  }

  removePrefix(index: number): void {
    const prefixes: Array<string> = [];
    for (let i = 0; i < this.editConfigData['prefixes'].length; i++) {
      if (index !== i) prefixes.push(this.editConfigData['prefixes'][i]);
    }
    this.editConfigData['prefixes'] = prefixes;
  }

  addBlacklist(): void {
    const newBlacklist: string = this.newBlacklist.trim();
    let found: boolean = false;
    for (let blacklist of this.editConfigData['blacklistFields']) {
      if (newBlacklist === blacklist) {
        found = true;
        break;
      }
    }
    if (!found && newBlacklist.length !== 0) {
      this.editConfigData['blacklistFields'].push(newBlacklist);
      this.newBlacklist = '';
    }
  }

  removeBlacklist(index: number): void {
    const blacklistFields: Array<string> = [];
    for (let i = 0; i < this.editConfigData['blacklistFields'].length; i++) {
      if (index !== i) blacklistFields.push(this.editConfigData['blacklistFields'][i]);
    }
    this.editConfigData['blacklistFields'] = blacklistFields;
  }

  addRequired(): void {
    const newRequired: string = this.newRequired.trim();
    let found: boolean = false;
    for (let required of this.editConfigData['requiredFields']) {
      if (newRequired === required) {
        found = true;
        break;
      }
    }
    if (!found && newRequired.length !== 0) {
      this.editConfigData['requiredFields'].push(newRequired);
      this.newRequired = '';
    }
  }

  removeRequired(index: number): void {
    const requiredFields: Array<string> = [];
    for (let i = 0; i < this.editConfigData['requiredFields'].length; i++) {
      if (index !== i) requiredFields.push(this.editConfigData['requiredFields'][i]);
    }
    this.editConfigData['requiredFields'] = requiredFields;
  }

  addUnmatch(): void {
    const newUnmatch: string = this.newUnmatch.trim();
    let found: boolean = false;
    for (let unmatch of this.editConfigData['unmatchFields']) {
      if (newUnmatch === unmatch) {
        found = true;
        break;
      }
    }
    if (!found && newUnmatch.length !== 0) {
      this.editConfigData['unmatchFields'].push(newUnmatch);
      this.newUnmatch = '';
    }
  }

  removeUnmatch(index: number): void {
    const unmatchFields: Array<string> = [];
    for (let i = 0; i < this.editConfigData['unmatchFields'].length; i++) {
      if (index !== i) unmatchFields.push(this.editConfigData['unmatchFields'][i]);
    }
    this.editConfigData['unmatchFields'] = unmatchFields;
  }

  editFieldRequirement(field: string, value: string) {
    this.editConfigData['fields'][field]['required'] = value;
  }

  addMessagePart(type: string) {
    if (type === 'url') {
      this.editConfigData['fields']['messageParts'].push('url');
    } else if (type === 'newline') {
      this.editConfigData['fields']['messageParts'].push('newline');
    }
    else if (type === 'tab') {
      this.editConfigData['fields']['messageParts'].push('tab');
    }
    else if (type === 'text') {
      this.editConfigData['fields']['messageParts'].push(`text:${this.rawTextInput}`);
      this.rawTextInput = '';
    }
    else if (type === 'field') {
      this.editConfigData['fields']['messageParts'].push(`field:${this.rawFieldInput.trim()}`);
      this.rawFieldInput = '';
    }
  }

  moveMessagePartLeft(index: number) {
    if (index === 0) return;
    const copy: string = this.editConfigData['fields']['messageParts'][index - 1];
    this.editConfigData['fields']['messageParts'][index - 1] = this.editConfigData['fields']['messageParts'][index];
    this.editConfigData['fields']['messageParts'][index] = copy;
  }

  moveMessagePartRight(index: number) {
    if (index === this.editConfigData['fields']['messageParts'].length - 1) return;
    const copy: string = this.editConfigData['fields']['messageParts'][index + 1];
    this.editConfigData['fields']['messageParts'][index + 1] = this.editConfigData['fields']['messageParts'][index];
    this.editConfigData['fields']['messageParts'][index] = copy;
  }

  removeMessagePart(index: number) {
    const messageParts: Array<string> = [];
    for (let i = 0; i < this.editConfigData['fields']['messageParts'].length; i++) {
      if (index !== i) {
        messageParts.push(this.editConfigData['fields']['messageParts'][i]);
      }
    }
    this.editConfigData['fields']['messageParts'] = messageParts;
  }

  duplicateUser(index: number) {
    this.showDupe = index;
    this.changeUser = false;
    this.editConfigIndex = null;
    this.toggleMatchedForm(null);
  }

  removeDupe() {
    this.showDupe = null;
  }

  async sendDomain(domain: string, config: object): Promise<void> {
    const loading: Loading = this.loadingCtrl.create({});
    loading.present();
    const payload: object = { domain: domain, formConfig: config };
    const result: object = await this.aws.invokeLambda(
      'SherloqAdminFEDuplicateComplianceConfig:v1',
      payload
    );
    if (!result['success']) {
      const alert: Alert = this.alertCtrl.create({
        message: result['response'],
        buttons: ['Ok'],
        enableBackdropDismiss: true
      });
      alert.present();
      loading.dismiss();
    } else {
      this.showDupe = null;
      const result: boolean = await this.reloadAllDomainData();
      if (!result) this.navCtrl.pop();
      loading.dismiss();
    }
  }

  verifyEdit(index: string): void {
    // make sure at least 1 prefix
    if (this.editConfigData['prefixes'].length === 0) {
      const alert: Alert = this.alertCtrl.create({
        message: 'Must have at least one prefix.',
        buttons: ['Ok'],
        enableBackdropDismiss: true
      });
      alert.present();
      return;
    }
    // make sure no blacklisted fields match form mapping fields
    for (let field in this.editConfigData['fields']) {
      if (this.editConfigData['blacklistFields'].indexOf(this.editConfigData['fields'][field]['text']) > -1) {
        const alert: Alert = this.alertCtrl.create({
          message: 'Blacklisted field cannot match form mapping field.',
          buttons: ['Ok'],
          enableBackdropDismiss: true
        });
        alert.present();
        return;
      }
    }
    // make sure no unmatch fields match form mapping fields
    for (let field in this.editConfigData['fields']) {
      if (this.editConfigData['unmatchFields'].indexOf(this.editConfigData['fields'][field]['text']) > -1) {
        const alert: Alert = this.alertCtrl.create({
          message: 'Unmatch field cannot match form mapping field.',
          buttons: ['Ok'],
          enableBackdropDismiss: true
        });
        alert.present();
        return;
      }
    }
    // make sure no blacklisted fields match unmatch fields
    for (let field of this.editConfigData['unmatchFields']) {
      if (this.editConfigData['blacklistFields'].indexOf(field) > -1) {
        const alert: Alert = this.alertCtrl.create({
          message: 'Blacklisted field cannot match unmatch field.',
          buttons: ['Ok'],
          enableBackdropDismiss: true
        });
        alert.present();
        return;
      }
    }
    // make sure no required fields match unmatch fields
    for (let field of this.editConfigData['requiredFields']) {
      if (this.editConfigData['unmatchFields'].indexOf(field) > -1) {
        const alert: Alert = this.alertCtrl.create({
          message: 'Blacklisted field cannot match required field.',
          buttons: ['Ok'],
          enableBackdropDismiss: true
        });
        alert.present();
        return;
      }
    }
    // make sure we have at least 1 message part
    if (!this.editConfigData['ignore'] && this.editConfigData['fields']['messageParts'].length === 0) {
      const alert: Alert = this.alertCtrl.create({
        message: 'Must have at least one message part.',
        buttons: ['Ok'],
        enableBackdropDismiss: true
      });
      alert.present();
      return;
    }
    // make sure no required form mapping fields are empty
    const fields: object = {};
    let count: number = 0;
    for (let field in this.editConfigData['fields']) {
      if (field === 'messageParts') {
        fields['messageParts'] = this.editConfigData['fields']['messageParts'];
        count++;
        continue;
      }
      const required: string = this.editConfigData['fields'][field]['required'];
      const text: string = this.editConfigData['fields'][field]['text'].trim();
      if (!this.editConfigData['ignore'] && required === 'true' && text.length === 0) {
        const alert: Alert = this.alertCtrl.create({
          message: 'Required form mapping field cannot be empty.',
          buttons: ['Ok'],
          enableBackdropDismiss: true
        });
        alert.present();
        return;
      }
      if (text.length > 0) {
        count++;
        fields[text] = { name: field, required: required === 'true' ? true : false };
      }
    }
    // make sure no two mapping fields are the same
    if (Object.keys(fields).length !== count) {
      const alert: Alert = this.alertCtrl.create({
        message: 'Form mapping field names cannot be identical.',
        buttons: ['Ok'],
        enableBackdropDismiss: true
      });
      alert.present();
      return;
    }
    // save it!
    const editConfigData: object = JSON.parse(JSON.stringify(this.editConfigData));
    editConfigData['fields'] = fields;
    delete editConfigData['matchedForms'];
    delete editConfigData['toggle'];
    if (!this.addConfig) {
      this.editBackendFormConfigs('update', editConfigData);
    } else {
      this.editBackendFormConfigs('add', editConfigData);
    }
    this.bringDownFormConfig();
  }

  async editBackendFormConfigs(type: string, formConfig: object): Promise<void> {
    const payload: object = { domain: this.domain['text'], formConfig };
    let lambda: string;
    if (type === 'add') lambda = 'SherloqAdminFEComplianceAddFormConfig:v1';
    if (type === 'update') lambda = 'SherloqAdminFEComplianceUpdateFormConfig:v1';
    if (type === 'remove') lambda = 'SherloqAdminFEComplianceRemoveFormConfig:v1';
    const loading: Loading = this.loadingCtrl.create({});
    loading.present();
    const result: object = await this.aws.invokeLambda(lambda, payload);
    if (!result['success']) {
      const alert: Alert = this.alertCtrl.create({
        message: result['response'],
        buttons: ['Ok'],
        enableBackdropDismiss: true
      });
      alert.present();
      this.navCtrl.pop();
    } else {
      const result: boolean = await this.reloadAllDomainData();
      if (!result) this.navCtrl.pop();
    }
    loading.dismiss();
  }

  async editBackendDomainUser(userId: string): Promise<void> {
    const payload: object = { domain: this.domain['text'], userId };
    const loading: Loading = this.loadingCtrl.create({});
    loading.present();
    const lambda: string = 'SherloqAdminFEComplianceUpdateDomainUser:v1';
    const result: object = await this.aws.invokeLambda(lambda, payload);
    if (!result['success']) {
      const alert: Alert = this.alertCtrl.create({
        message: result['response'],
        buttons: ['Ok'],
        enableBackdropDismiss: true
      });
      alert.present();
      this.navCtrl.pop();
    } else {
      const result: boolean = await this.reloadAllDomainData();
      if (!result) this.navCtrl.pop();
    }
    loading.dismiss();
  }

  toggleUnmatchedForm(path: string) {
    // first, get current status
    let current: boolean;
    for (let formPath of this.urlConfig['unmatchedForms']) {
      if (path === formPath['path']) {
        current = formPath['toggle'] ? false : true;
        break;
      }
    }
    // second, flip the toggle
    for (let formPath of this.urlConfig['unmatchedForms']) {
      if (path !== formPath['path']) {
        formPath['toggle'] = false;
      } else {
        formPath['toggle'] = current ? true : false;
      }
    }
  }

  toggleMatchedForm(index: number) {
    if (index !== null) {
      this.bringDownFormConfig();
      this.showDupe = null;
    }
    for (let i = 0; i < this.urlConfig['formConfigs'].length; i++) {
      if (index === null) {
        this.urlConfig['formConfigs'][i]['toggle'] = false;
      } else {
        if (i !== index) {
          this.urlConfig['formConfigs'][i]['toggle'] = false;
        } else {
          this.urlConfig['formConfigs'][i]['toggle'] = !this.urlConfig['formConfigs'][i]['toggle'] ? true : false;
        }
      }
    }
  }

}



// WEBPACK FOOTER //
// ./src/pages/compliance-domain/compliance-domain.ts